/*
 * Layered org-chart layout (pure — no Office.js imports, unit-tested).
 *
 * Emits primitives (see primitives.ts) for an M&A/legal-entity style chart:
 * depth from parent chain, bottom-up subtree widths, top-down x assignment
 * with parents centered over their children, elbow connectors with optional
 * ownership-percentage labels. Deterministic for a given input.
 */

import {
  BoxPrim,
  DEFAULT_PALETTE,
  DEFAULT_REGION,
  LinePrim,
  Palette,
  Primitive,
  Region,
} from "./primitives";

export interface OrgNode {
  id: string | number;
  label: string;
  sublabel?: string;
  parent?: string | number;
  ownership_pct?: number;
}

/* Layout constants (points). */
const GUTTER = 24; // horizontal gap between sibling subtrees
const NODE_W_MIN = 110;
const NODE_W_MAX = 190;
const NODE_H = 44;
const NODE_H_SUB = 52; // row height when any node at that depth has a sublabel
const ROW_GAP_MIN = 40;
const CONNECTOR_COLOR = "94A3B8";
const CONNECTOR_WEIGHT = 1.25;

interface TreeNode {
  node: OrgNode;
  depth: number;
  children: TreeNode[];
  subtreeW: number;
  cx: number; // box center x
}

export function orgChartLayout(nodes: OrgNode[], region?: Region, palette?: Palette): Primitive[] {
  const reg = region || DEFAULT_REGION;
  const pal = palette || DEFAULT_PALETTE;
  if (!nodes || nodes.length === 0) return [];

  // Index nodes and wire up children in input order (deterministic).
  const byId: { [id: string]: TreeNode } = {};
  const all: TreeNode[] = [];
  for (let i = 0; i < nodes.length; i++) {
    const t: TreeNode = { node: nodes[i], depth: 0, children: [], subtreeW: 0, cx: 0 };
    byId[String(nodes[i].id)] = t;
    all.push(t);
  }
  const roots: TreeNode[] = [];
  for (let i = 0; i < all.length; i++) {
    const n = all[i].node;
    const parent = n.parent != null ? byId[String(n.parent)] : undefined;
    if (parent && parent !== all[i]) parent.children.push(all[i]);
    else roots.push(all[i]); // multiple roots become depth-0 siblings
  }

  // Depth via parent chain (walk down from roots; cycle-safe by construction).
  const rows: TreeNode[][] = [];
  const setDepth = (t: TreeNode, depth: number) => {
    t.depth = depth;
    if (!rows[depth]) rows[depth] = [];
    rows[depth].push(t);
    for (let i = 0; i < t.children.length; i++) setDepth(t.children[i], depth + 1);
  };
  for (let i = 0; i < roots.length; i++) setDepth(roots[i], 0);

  // Node width from the widest row that must fit the region width.
  let maxRow = 1;
  for (let d = 0; d < rows.length; d++) if (rows[d].length > maxRow) maxRow = rows[d].length;
  const idealW = (reg.w - (maxRow - 1) * GUTTER) / maxRow;
  const nodeW = Math.max(NODE_W_MIN, Math.min(NODE_W_MAX, idealW));

  // Per-depth row heights and y positions: rows spaced evenly, gap >= 40pt.
  const rowH: number[] = [];
  let sumH = 0;
  for (let d = 0; d < rows.length; d++) {
    let hasSub = false;
    for (let i = 0; i < rows[d].length; i++) if (rows[d][i].node.sublabel) hasSub = true;
    rowH[d] = hasSub ? NODE_H_SUB : NODE_H;
    sumH += rowH[d];
  }
  const rowY: number[] = [];
  if (rows.length === 1) {
    rowY[0] = reg.y + (reg.h - rowH[0]) / 2;
  } else {
    const gap = Math.max(ROW_GAP_MIN, (reg.h - sumH) / (rows.length - 1));
    let y = reg.y;
    for (let d = 0; d < rows.length; d++) {
      rowY[d] = y;
      y += rowH[d] + gap;
    }
  }

  // Bottom-up subtree widths.
  const measure = (t: TreeNode): number => {
    if (t.children.length === 0) {
      t.subtreeW = nodeW;
    } else {
      let span = (t.children.length - 1) * GUTTER;
      for (let i = 0; i < t.children.length; i++) span += measure(t.children[i]);
      t.subtreeW = Math.max(nodeW, span);
    }
    return t.subtreeW;
  };
  let totalW = (roots.length - 1) * GUTTER;
  for (let i = 0; i < roots.length; i++) totalW += measure(roots[i]);

  // Top-down x assignment: children packed with the gutter, parent centered
  // over its children's span; leaves take the node width.
  const assign = (t: TreeNode, left: number) => {
    if (t.children.length === 0) {
      t.cx = left + t.subtreeW / 2;
      return;
    }
    let span = (t.children.length - 1) * GUTTER;
    for (let i = 0; i < t.children.length; i++) span += t.children[i].subtreeW;
    let cursor = left + (t.subtreeW - span) / 2;
    for (let i = 0; i < t.children.length; i++) {
      assign(t.children[i], cursor);
      cursor += t.children[i].subtreeW + GUTTER;
    }
    t.cx = (t.children[0].cx + t.children[t.children.length - 1].cx) / 2;
  };
  let cursor = reg.x + Math.max(0, (reg.w - totalW) / 2);
  for (let i = 0; i < roots.length; i++) {
    assign(roots[i], cursor);
    cursor += roots[i].subtreeW + GUTTER;
  }

  // If the tree is wider than the region, scale all x/w down proportionally.
  // (Node width can drop below the 80pt floor for very wide trees — no-overlap
  // and stay-in-region win over the floor.)
  let scale = 1;
  if (totalW > reg.w) {
    scale = reg.w / totalW;
    for (let i = 0; i < all.length; i++) {
      all[i].cx = reg.x + (all[i].cx - reg.x) * scale;
    }
  }
  const boxW = nodeW * scale;

  const boxOf = (t: TreeNode) => ({
    x: t.cx - boxW / 2,
    y: rowY[t.depth],
    w: boxW,
    h: rowH[t.depth],
  });

  // Connectors first (so node boxes render on top), then node boxes.
  const prims: Primitive[] = [];
  for (let i = 0; i < all.length; i++) {
    const parent = all[i];
    const pb = boxOf(parent);
    for (let c = 0; c < parent.children.length; c++) {
      const child = parent.children[c];
      const cb = boxOf(child);
      const px = pb.x + pb.w / 2;
      const cx = cb.x + cb.w / 2;
      const topY = pb.y + pb.h;
      const botY = cb.y;
      const midY = (topY + botY) / 2;
      const lineStyle = { lineColor: CONNECTOR_COLOR, lineWeight: CONNECTOR_WEIGHT, fontSize: 9, fontColor: pal.ink };
      const pct = child.node.ownership_pct;
      prims.push(
        { kind: "line", x1: px, y1: topY, x2: px, y2: midY, style: lineStyle } as LinePrim,
        { kind: "line", x1: px, y1: midY, x2: cx, y2: midY, style: lineStyle } as LinePrim,
        {
          kind: "line",
          x1: cx,
          y1: midY,
          x2: cx,
          y2: botY,
          label: pct != null ? pct + "%" : undefined,
          style: lineStyle,
        } as LinePrim
      );
    }
  }
  for (let i = 0; i < all.length; i++) {
    const t = all[i];
    const b = boxOf(t);
    prims.push({
      kind: "roundRect",
      x: b.x,
      y: b.y,
      w: b.w,
      h: b.h,
      text: t.node.label,
      sub: t.node.sublabel,
      style: {
        fill: t.depth === 0 ? pal.primary : pal.secondary,
        fontColor: t.depth === 0 ? pal.paper : pal.ink,
        fontSize: 12,
        bold: t.depth === 0,
        align: "center",
      },
    } as BoxPrim);
  }
  return prims;
}
